import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useFormik } from "formik";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import { usePrevious } from "../../hooks/usePrevious";
import {
  Option,
  TVacancyFormValues,
  VACANCY_CANDIDATE_VISIBILITY,
  VACANCY_SELECTION_METHOD,
  VACANCY_START_AVAILABILITY,
} from "../../models/vacancy";
import { AUTH_ROUTE } from "../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../store/application/slice";
import { getAllCenters } from "../../store/centers/thunks";
import { TAllCentersList } from "../../store/centers/types";
import {
  resetAllActiveCandidatesAction,
  setSelectedCentreDetailsAction,
} from "../../store/vacancy/slice";
import {
  createVacancyThunk,
  getAllActiveCandidates,
  getAllPositions,
  getCentreDetailsForVacancyThunk,
  getVacancyById,
  updateVacancyThunk,
} from "../../store/vacancy/thunks";
import {
  TCandidate,
  TPosition,
  TVacancy,
  VacancyFormAPIPayload,
} from "../../store/vacancy/types";
import { createValidationSchema } from "../../utils/createValidationSchema";
import storage from "../../utils/storage";
import { capitalizeName } from "../../utils/stringUtils";
import SaveNotification from "../SaveNotification";
import PopupModal from "../UI/Popup";
import { areTimeframesValid } from "../UI/TimeframeSelector/timeframeHelpers";
import {
  transformAPIToFormTimeframes,
  transformTimeframesForAPI,
} from "./timeframeApiHelper";
import VacancyFields from "./VacancyFields";
import VacancyMessage, { VacancyMessageType } from "./VacancyInfoModal";

const CandidateSelectionOptions: Option[] = [
  {
    label: "First Candidate to Accept",
    value: VACANCY_SELECTION_METHOD.FIRST_COME_FIRST_SERVED,
  },
  {
    label: "I Choose the Candidate",
    value: VACANCY_SELECTION_METHOD.MANUAL_SELECTION,
  },
];

// response timeframe in mins
const responseTimeframes: Option[] = [
  { label: "15 mins", value: 15 },
  { label: "30 mins", value: 30 },
  { label: "1 hr", value: 60 },
  { label: "1 day", value: 1440 }, // 24 * 60
  { label: "2 days", value: 2880 }, // 2 * 24 * 60,
  { label: "3 days", value: 4320 }, // 3 * 24 * 60,
];

const extendedResponseTimeframes: Option[] = [
  ...responseTimeframes,
  { label: "4 days", value: 5760 },
  { label: "5 days", value: 7200 },
];

const formatTimeframesForCandidatesApi = (
  timeframes: {
    date: [Date | null, Date | null] | null;
    start_time: string;
    end_time: string;
  }[]
) =>
  timeframes
    .filter(
      (tf) =>
        tf.date &&
        tf.date[0] !== null &&
        tf.date[1] !== null &&
        tf.start_time &&
        tf.end_time
    )
    .map(({ date, start_time, end_time }) => ({
      start_date: format(new Date(date![0]!), "dd MMM yyyy"),
      end_date: format(new Date(date![1]!), "dd MMM yyyy"),
      start_time,
      end_time,
    }));

const getCentreUUID = (
  centres: { id: number; uuid: string }[],
  centreId: number
): string => centres.find((centre) => centre.id === centreId)?.uuid ?? "";

type ModalType =
  | VacancyMessageType.CANDIDATE_UNAVAILABLE
  | VacancyMessageType.NO_MATCHES_FOUND
  | null;

const VacancyForm: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [validCandidatesAvailable, setValidCandidatesAvailable] =
    useState(false);
  const isAdmin = useMemo(
    () => location.pathname.includes("admin"),
    [location.pathname]
  );
  const isEdit = useMemo(
    () => location.pathname.includes("edit"),
    [location.pathname]
  );

  const [showNotification, setShowNotification] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  const selectedVacancyId = useMemo(
    () => storage.get<string>(STORAGE_KEYS.SELECTED_VACANCY_ID),
    []
  );
  const duplicateVacancyId = useMemo(
    () => storage.get<string>(STORAGE_KEYS.DUPLICATE_VACANCY_ID),
    []
  );
  const isDuplicate = useMemo(
    () => !!duplicateVacancyId && !isEdit,
    [duplicateVacancyId, isEdit]
  );

  const {
    selectedVacancy,
    positions,
    allActiveCandidates,
    selectedCentreDetails: selectedCentreDetailsUuid,
    allCentreDetailsList, //only used for list in the select centre
  } = useSelector((state) => state.vacancy);
  const allCentersList = useSelector((state) => state.Centres.allCenters);

  const allCentreOptions = useMemo<Option[]>(
    () =>
      allCentreDetailsList.map((c) => ({
        label: c.centre_name,
        value: c.id,
      })),
    [allCentreDetailsList]
  );

  const candidatePositions = useMemo(
    () => positions.filter((p: TPosition) => !p.is_alternative),
    [positions]
  );

  const allActiveCandidateOptions = useMemo(
    () =>
      allActiveCandidates?.map((candidate: TCandidate) => ({
        label: `${capitalizeName(candidate.first_name ?? "")} ${capitalizeName(candidate.last_name ?? "")}`.trim(),
        value: candidate.id,
        rating: isAdmin
          ? candidate?.rating?.average_rating || 0
          : candidate?.rating?.latest_rating ||
            candidate?.rating?.average_rating ||
            0,
        ratingColor:
          isAdmin || !candidate?.rating?.latest_rating ? "#aaaaaa" : "#51C2B9",
      })) ?? [],
    [allActiveCandidates]
  );
  const fetchCenters = async (): Promise<TAllCentersList | null> => {
    const result = await dispatch(getAllCenters());
    if (isFulfilled(result)) {
      return result.payload?.data?.centres ?? [];
    } else {
      console.warn("Failed to fetch centers");
      return null;
    }
  };

  const fetchCentersForLists = async () => {
    await dispatch(getCentreDetailsForVacancyThunk());
  };

  const fetchVacancy = async (vacancyId: string) => {
    const result = await dispatch(getVacancyById(vacancyId));
    if (isFulfilled(result)) {
      return result.payload?.vacancy ?? null;
    } else {
      console.warn("Failed to fetch vacancy data");
      return null;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (
        (isEdit && !selectedVacancyId) ||
        (isDuplicate && !duplicateVacancyId)
      ) {
        navigate(
          isAdmin
            ? AUTH_ROUTE.ADMIN_URGENT_VACANCIES
            : AUTH_ROUTE.CLIENT_VACANCIES
        );
        return;
      }

      dispatch(showTransparentPreloaderAction());

      try {
        await fetchCentersForLists();

        // Step 1: Get All Centers
        const centers = await fetchCenters();
        if (!centers) return;

        // Step 2: Get All Positions
        await dispatch(getAllPositions());
        // Step 3: Get Vacancy by ID
        const vacancyId = isDuplicate
          ? duplicateVacancyId
          : isEdit
          ? selectedVacancyId
          : null;
        const vacancyData = vacancyId ? await fetchVacancy(vacancyId) : null;

        // Step 4: Extract center UUID using fresh centers result
        const centre_uuid = getCentreUUID(
          centers as any,
          vacancyData?.centre_id as number
        );

        const timeframes = vacancyData?.timeframes
          ? transformAPIToFormTimeframes(isEdit, vacancyData.timeframes)
          : [{ id: Date.now(), date: null, start_time: "", end_time: "" }];

        const formattedTimeframes =
          formatTimeframesForCandidatesApi(timeframes);

        // Step 5: Get All Active Candidates
        if (centre_uuid && formattedTimeframes.length > 0) {
          setValidCandidatesAvailable(true);
          await dispatch(
            getAllActiveCandidates({
              centre_uuid,
              timeframes: formattedTimeframes,
              position_uuids:
                vacancyData?.vacancy_positions
                  ?.filter((p: TPosition) => !p.is_alternative)
                  ?.map((p: TPosition) => p.uuid) ?? [],
              alternate_position_legacy:
                vacancyData?.alternate_position_legacy ?? "",
              vacancy_id: vacancyData?.id || null,
            })
          );
        }
      } catch (error) {
        console.error("Error during vacancy initialization:", error);
      } finally {
        dispatch(hideTransparentPreloaderAction());
      }
    };

    initialize();
  }, [dispatch, duplicateVacancyId, selectedVacancyId, isEdit]);

  const getVacancyFormValues = ({
    selectedVacancy,
    isEdit = false,
  }: {
    selectedVacancy?: TVacancy | null;
    isEdit?: boolean;
  }): TVacancyFormValues => {
    return {
      isEdit,
      created_at: selectedVacancy?.created_at ?? null,
      centre_id:
        selectedVacancy?.centre_id ??
        (allCentreOptions.length === 1
          ? (allCentreOptions[0].value as number)
          : null),
      timeline_type: selectedVacancy?.timeline_type ?? "",
      timeframes: selectedVacancy?.timeframes
        ? transformAPIToFormTimeframes(isEdit, selectedVacancy.timeframes)
        : [{ id: Date.now(), date: null, start_time: "", end_time: "" }],
      position_uuids: selectedVacancy?.vacancy_positions?.some(
        (p: TPosition) => !p.is_alternative
      )
        ? selectedVacancy.vacancy_positions
            .filter((p: TPosition) => !p.is_alternative)
            .map((p: TPosition) => p.uuid)
        : [],
      alternate_position_legacy:
        selectedVacancy?.alternate_position_legacy ?? "",
      visibility_type: selectedVacancy?.visibility_type ?? "",
      selected_candidate_id:
        selectedVacancy?.visibility_type ===
        VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE
          ? selectedVacancy?.selected_candidate_id || null
          : null,
      response_timeframe_check: !!selectedVacancy?.response_timeframe,
      response_timeframe:
        selectedVacancy?.response_timeframe ??
        (responseTimeframes[0].value as number),
      all_response_timeframe_check: !!selectedVacancy?.all_response_timeframe_check,
      all_response_timeframe:
        selectedVacancy?.all_response_timeframe ??
        (responseTimeframes[0].value as number),
      selection_method:
        selectedVacancy?.selection_method ?? CandidateSelectionOptions[0].value,
      allowance: selectedVacancy?.allowance ?? null,
      allowance_amount: selectedVacancy?.allowance ?? null,
      shift_notes: selectedVacancy?.shift_notes ?? ""
    };
  };

  const validatePreferredCandidate = (): boolean => {
    const count =
      allCentersList.find((c) => c.id === values.centre_id)
        ?.preferred_candidates_count ?? 0;
    return count > 0;
  };

  /** Submit the form for vacancy-details */
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setValues,
    setFieldValue,
    setErrors,
  } = useFormik<TVacancyFormValues>({
    validationSchema: createValidationSchema("vacancy"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: getVacancyFormValues({
      selectedVacancy: null,
      isEdit: false,
    }),
    onSubmit: async (values: TVacancyFormValues) => {
      // 0. Validate timeframes
      const validationResult = areTimeframesValid(
        values.timeframes,
        values.timeline_type as VACANCY_START_AVAILABILITY,
        isEdit,
        null
      );
      if (!validationResult.valid) {
        const updatedTimeframeErrors = [...((errors.timeframes as any) || [])];
        updatedTimeframeErrors[validationResult.index || 0] =
          validationResult.error;

        setErrors({
          ...errors,
          timeframes: updatedTimeframeErrors,
        });
        return;
      }
      const isPreferredVisibility =
        values.visibility_type ===
        VACANCY_CANDIDATE_VISIBILITY.PREFERRED_CANDIDATES;

      const isSelectedVisibility =
        values.visibility_type ===
        VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE;

      const shouldFetchActiveCandidates =
        isPreferredVisibility || isSelectedVisibility;

      if (isPreferredVisibility && !validatePreferredCandidate()) {
        setActiveModal(VacancyMessageType.NO_MATCHES_FOUND);
        return;
      }

      if (shouldFetchActiveCandidates) {
        const centre_uuid = getCentreUUID(
          allCentersList,
          values.centre_id as number
        );
        const formattedTimeframes = formatTimeframesForCandidatesApi(
          values.timeframes
        );

        dispatch(showTransparentPreloaderAction());
        const result = await dispatch(
          getAllActiveCandidates({
            centre_uuid,
            timeframes: formattedTimeframes,
            position_uuids: values.position_uuids,
            alternate_position_legacy: values.alternate_position_legacy,
            visibility_type: values?.visibility_type,
          })
        );
        dispatch(hideTransparentPreloaderAction());

        if (!isFulfilled(result)) {
          console.error("Failed to fetch active candidates.");
          return;
        }

        if (
          isPreferredVisibility &&
          result.payload.data.preffered_candidates === true
        ) {
          setActiveModal(VacancyMessageType.NO_MATCHES_FOUND);
          return;
        }
        
        const currentActiveAllCandidates = result.payload.data.candidates || [];        
        const isCandidateAvailable = currentActiveAllCandidates.some(
          (candidate) => candidate.id === values.selected_candidate_id
        );

        if (isSelectedVisibility && !isCandidateAvailable) {
          setActiveModal(VacancyMessageType.CANDIDATE_UNAVAILABLE);
          return;
        }
      }

      const apiTimeframes = transformTimeframesForAPI(
        values.timeframes,
        values.timeline_type as VACANCY_START_AVAILABILITY
      );

      const payload: VacancyFormAPIPayload = {
        ...values,
        timeframes: apiTimeframes as [],
      };

      // Step 4: Dispatch API Call for Creating or Updating Vacancy
      try {
        const result =
          isEdit && selectedVacancy?.uuid
            ? await dispatch(
                updateVacancyThunk({
                  uuid: selectedVacancy.uuid,
                  data: payload,
                })
              )
            : await dispatch(createVacancyThunk(payload));

        if (isFulfilled(result)) {
          setShowNotification(true);
          setTimeout(
            () =>
              navigate(
                isAdmin
                  ? AUTH_ROUTE.ADMIN_URGENT_VACANCIES
                  : AUTH_ROUTE.CLIENT_VACANCIES
              ),
            3000
          );
          storage.remove(STORAGE_KEYS.SELECTED_VACANCY_ID);
          storage.remove(STORAGE_KEYS.DUPLICATE_VACANCY_ID);
        }
      } catch (error) {
        console.error(
          "Could not connect to services at the moment. Please try again."
        );
      }
    },
  });

  useEffect(() => {
    if ((isEdit || isDuplicate) && selectedVacancy) {
      setValues(
        getVacancyFormValues({
          selectedVacancy,
          isEdit: isEdit,
        })
      );
    }
  }, [isEdit, isDuplicate, selectedVacancy, allCentreOptions, setValues]);

  const onClickClose = useCallback(() => setActiveModal(null), []);

  const validTofetchCandidates = useCallback(async () => {
    const isSelectedCandidateVisibility =
      values.visibility_type ===
      VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE;

    if (!isSelectedCandidateVisibility) {
      await dispatch(resetAllActiveCandidatesAction());
      return false;
    }

    const isValidCentre = !!values.centre_id;

    const centre_uuid = getCentreUUID(
      allCentersList,
      values.centre_id as number
    );
    if (!centre_uuid) {
      await dispatch(resetAllActiveCandidatesAction());
      return false;
    }

    const hasTimeframes =
      Array.isArray(values.timeframes) && values.timeframes.length > 0;
    const hasValidTimeframes = values.timeframes.every(
      (tf) => tf.date?.[0] && tf.date?.[1] && tf.start_time && tf.end_time
    );
    const hasTimelineType = !!values.timeline_type;

    if (
      !isValidCentre ||
      !hasTimeframes ||
      !hasValidTimeframes ||
      !hasTimelineType
    ) {
      setFieldValue("selected_candidate_id", null);
      dispatch(resetAllActiveCandidatesAction());
      return false;
    }

    const validationResult = areTimeframesValid(
      values.timeframes,
      values.timeline_type as VACANCY_START_AVAILABILITY,
      isEdit,
      null
    );

    if (!validationResult.valid) {
      const updatedTimeframeErrors = [...((errors.timeframes as any) || [])];
      updatedTimeframeErrors[validationResult.index || 0] =
        validationResult.error;

      setErrors({
        ...errors,
        timeframes: updatedTimeframeErrors,
      });

      // Reset selected candidate and available candidates
      setFieldValue("selected_candidate_id", null);
      dispatch(resetAllActiveCandidatesAction());
      return false;
    }

    // Clear previous errors
    if (errors.timeframes) {
      setErrors({ ...errors, timeframes: undefined });
    }

    return true;
  }, [
    values.centre_id,
    values.visibility_type,
    values.timeframes,
    values.timeline_type,
    isEdit,
    dispatch,
    errors.timeframes,
    allCentersList,
  ]);

  const handleFetchCandidates = useCallback(async () => {
    const centre_uuid = getCentreUUID(
      allCentersList,
      values.centre_id as number
    );

    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllActiveCandidates({
        centre_uuid,
        timeframes: formatTimeframesForCandidatesApi(values.timeframes),
        position_uuids: values.position_uuids,
        alternate_position_legacy: values.alternate_position_legacy,
      })
    );
    dispatch(hideTransparentPreloaderAction());
  }, [
    values.centre_id,
    values.visibility_type,
    values.timeframes,
    values.timeline_type,
    values.position_uuids,
    values.alternate_position_legacy,
    isEdit,
    dispatch,
    errors.timeframes,
    allCentersList,
  ]);

  useEffect(() => {
    async function checkCandidatesSelection() {
      const isValid = await validTofetchCandidates();
      setValidCandidatesAvailable(isValid);
    }
    checkCandidatesSelection();
  }, [validTofetchCandidates]);

  const prevCentreId = usePrevious(values?.centre_id);

  // Get matching centre ID from UUID or single-option fallback
  const storedCentreId = storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID);
  const centreIdFromUuid = allCentersList?.find(
    (centre) => centre?.uuid === selectedCentreDetailsUuid
  )?.id;
  const singleCentreId =
    allCentreOptions.length === 1 ? allCentreOptions[0]?.value : null;

  // Memo the resolved centre id to prevent change-triggering effects
  const resolvedCentreId = storedCentreId || centreIdFromUuid || singleCentreId;

  // Prevent infinite loop: Only update form if value is truly different
  useEffect(() => {
    if (resolvedCentreId && values?.centre_id !== resolvedCentreId) {
      setFieldValue("centre_id", resolvedCentreId);
    }
  }, [resolvedCentreId]); // ❗Do NOT include values.centre_id in deps

  // Dispatch only if selected centre UUID differs
  useEffect(() => {
    if (values?.centre_id && prevCentreId !== values?.centre_id) {
      const selectedCentre = allCentersList?.find(
        (c) => c.id === values.centre_id || c.uuid === values.centre_id
      );

      // Check if dispatch is actually needed
      if (
        selectedCentre &&
        selectedCentre.uuid !== selectedCentreDetailsUuid &&
        !isEdit
      ) {
        dispatch(setSelectedCentreDetailsAction(selectedCentre));
      }
    }
  }, [
    values?.centre_id,
    allCentersList,
    prevCentreId,
    selectedCentreDetailsUuid,
  ]);

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={!isEdit ? "Shared With Candidates" : "Saved"}
          onClose={() => setShowNotification(false)}
        />
      )}
      <VacancyFields
        isAdmin={isAdmin}
        isEdit={isEdit}
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        allCentreOptions={allCentreOptions}
        CandidateSelectionOptions={CandidateSelectionOptions}
        candidatePositions={candidatePositions}
        allActiveCandidateOptions={allActiveCandidateOptions}
        responseTimeframes={responseTimeframes}
        allResponseTimeframes={extendedResponseTimeframes}
        disableCandidatesSelection={!validCandidatesAvailable}
        fetchActiveCandidates={handleFetchCandidates}
        onSubmit={handleSubmit}
      />
      {/* No preferred candidates and Candidate Unavailable Modal */}
      <PopupModal
        visible={!!activeModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        <VacancyMessage type={activeModal!} onReturn={onClickClose} />
      </PopupModal>
    </>
  );
};

export default VacancyForm;
